import React, { useEffect, useRef, useState } from "react";
import { Keyboard, X, Backspace, ArrowRight } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";

// Standard Arabic keyboard layout (physical AZERTY-style Arabic keys).
// Row order mirrors the top-to-bottom row order on a typical Arabic PC keyboard.
const ROWS = [
    ["ذ", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩", "٠", "-", "="],
    ["ض", "ص", "ث", "ق", "ف", "غ", "ع", "ه", "خ", "ح", "ج", "د"],
    ["ش", "س", "ي", "ب", "ل", "ا", "ت", "ن", "م", "ك", "ط"],
    ["ئ", "ء", "ؤ", "ر", "لا", "ى", "ة", "و", "ز", "ظ"],
];
const SHIFT_ROWS = [
    ["ّ", "!", "@", "#", "$", "%", "^", "&", "*", ")", "(", "_", "+"],
    ["َ", "ً", "ُ", "ٌ", "لإ", "إ", "‘", "÷", "×", "؛", "<", ">"],
    ["ِ", "ٍ", "ي", "ب", "لأ", "أ", "ـ", "،", "/", ":", '"'],
    ["ْ", "ٔ", "ٕ", "ٓ", "لآ", "آ", "’", ",", ".", "؟"],
];

/**
 * Global Arabic on-screen keyboard. Rendered once (typically inside Layout).
 * A floating toggle button opens a draggable panel that types into the
 * last-focused text input / textarea / contentEditable element.
 *
 * The keyboard tracks `document.activeElement` on focus events so we always
 * know where to insert characters even after the user clicks a key
 * (which briefly steals focus away from the target input).
 */
export default function ArabicKeyboard() {
    const { t, lang } = useI18n();
    const [open, setOpen] = useState(false);
    const [shift, setShift] = useState(false);
    const targetRef = useRef(null);
    // Re-open persistence — remember position between opens.
    const [pos, setPos] = useState({ x: null, y: null });

    // Track focused input so keys type into the right target.
    useEffect(() => {
        const onFocus = (e) => {
            const el = e.target;
            if (!el) return;
            const tag = (el.tagName || "").toLowerCase();
            if (tag === "input" && ["text", "search", "email", "tel", "url", ""].includes((el.type || "").toLowerCase())) {
                targetRef.current = el;
            } else if (tag === "textarea") {
                targetRef.current = el;
            } else if (el.isContentEditable) {
                targetRef.current = el;
            }
        };
        document.addEventListener("focusin", onFocus, true);
        return () => document.removeEventListener("focusin", onFocus, true);
    }, []);

    const insert = (ch) => {
        const el = targetRef.current;
        if (!el) return;
        if (el.isContentEditable) {
            document.execCommand("insertText", false, ch);
            return;
        }
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        const before = el.value.slice(0, start);
        const after = el.value.slice(end);
        const nativeSetter = Object.getOwnPropertyDescriptor(
            el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype,
            "value",
        ).set;
        nativeSetter.call(el, before + ch + after);
        el.dispatchEvent(new Event("input", { bubbles: true }));
        const caret = start + ch.length;
        el.setSelectionRange(caret, caret);
        el.focus();
    };
    const backspace = () => {
        const el = targetRef.current;
        if (!el || el.isContentEditable) return;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        if (start === 0 && end === 0) return;
        const cutStart = start === end ? Math.max(0, start - 1) : start;
        const before = el.value.slice(0, cutStart);
        const after = el.value.slice(end);
        const nativeSetter = Object.getOwnPropertyDescriptor(
            el.tagName === "TEXTAREA" ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype,
            "value",
        ).set;
        nativeSetter.call(el, before + after);
        el.dispatchEvent(new Event("input", { bubbles: true }));
        el.setSelectionRange(cutStart, cutStart);
        el.focus();
    };
    const space = () => insert(" ");

    // Keep the button visible in all languages but nudge label based on lang.
    const label = lang === "ar" ? "لوحة المفاتيح العربية" : lang === "fr" ? "Clavier arabe" : "Arabic keyboard";

    // Draggable positioning — snap to sensible defaults.
    const startDrag = (e) => {
        const panel = e.currentTarget.parentElement;
        const startX = e.clientX, startY = e.clientY;
        const rect = panel.getBoundingClientRect();
        const origX = rect.left, origY = rect.top;
        const move = (ev) => {
            setPos({
                x: Math.max(8, Math.min(window.innerWidth - rect.width - 8, origX + (ev.clientX - startX))),
                y: Math.max(8, Math.min(window.innerHeight - rect.height - 8, origY + (ev.clientY - startY))),
            });
        };
        const up = () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
        };
        window.addEventListener("mousemove", move);
        window.addEventListener("mouseup", up);
    };

    const rows = shift ? SHIFT_ROWS : ROWS;

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                title={label}
                aria-label={label}
                data-testid="arabic-keyboard-toggle"
                className="fixed bottom-20 md:bottom-4 end-4 z-40 h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center hover:bg-primary/90 transition-transform hover:scale-105"
                style={{ display: open ? "none" : "flex" }}
            >
                <Keyboard size={20} weight="bold" />
                <span className="absolute -top-1 -end-1 bg-background text-foreground text-[8px] font-black px-1 rounded-sm border border-border">ع</span>
            </button>

            {open && (
                <div
                    role="dialog"
                    aria-label={label}
                    data-testid="arabic-keyboard-panel"
                    dir="rtl"
                    className="fixed z-50 bg-background border border-border shadow-2xl rounded-md select-none"
                    style={{
                        left: pos.x ?? undefined,
                        top: pos.y ?? undefined,
                        right: pos.x == null ? 16 : undefined,
                        bottom: pos.y == null ? 16 : undefined,
                        width: "min(680px, 96vw)",
                    }}
                >
                    <div
                        onMouseDown={startDrag}
                        className="flex items-center justify-between px-3 py-2 border-b border-border cursor-move bg-secondary"
                        dir="ltr"
                    >
                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
                            <Keyboard size={14} weight="bold" />
                            {label}
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            data-testid="arabic-keyboard-close"
                            className="h-7 w-7 flex items-center justify-center rounded-sm hover:bg-background"
                            aria-label={t("close") || "Close"}
                        >
                            <X size={14} weight="bold" />
                        </button>
                    </div>
                    <div className="p-2 space-y-1.5" dir="rtl">
                        {rows.map((row, ri) => (
                            <div key={ri} className="flex gap-1 justify-center">
                                {row.map((ch, ci) => (
                                    <button
                                        key={ci}
                                        type="button"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => insert(ch)}
                                        data-testid={`ar-key-${ri}-${ci}`}
                                        className="min-w-[38px] h-11 px-2 rounded-sm bg-secondary hover:bg-primary hover:text-primary-foreground text-lg font-medium border border-border transition-colors"
                                    >
                                        {ch}
                                    </button>
                                ))}
                            </div>
                        ))}
                        <div className="flex gap-1 justify-center pt-1">
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => setShift((s) => !s)}
                                data-testid="ar-key-shift"
                                className={`h-11 px-4 rounded-sm border border-border text-xs uppercase tracking-widest ${shift ? "bg-primary text-primary-foreground" : "bg-secondary hover:bg-muted"}`}
                            >
                                Shift {shift ? "•" : ""}
                            </button>
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={space}
                                data-testid="ar-key-space"
                                className="h-11 flex-1 max-w-[280px] rounded-sm bg-secondary hover:bg-muted border border-border text-xs uppercase tracking-widest"
                            >
                                {t("space") || "Space"}
                            </button>
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={backspace}
                                data-testid="ar-key-backspace"
                                className="h-11 px-4 rounded-sm bg-secondary hover:bg-muted border border-border flex items-center gap-1 text-xs uppercase tracking-widest"
                            >
                                <Backspace size={14} weight="bold" />
                                {t("backspace") || "Back"}
                            </button>
                            <button
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => insert("\u200F")}
                                data-testid="ar-key-rtl"
                                title="Right-to-left mark"
                                className="h-11 px-3 rounded-sm bg-secondary hover:bg-muted border border-border flex items-center gap-1 text-xs uppercase tracking-widest"
                            >
                                <ArrowRight size={14} weight="bold" />RTL
                            </button>
                        </div>
                        {!targetRef.current && (
                            <div className="text-[11px] text-muted-foreground text-center pt-1" dir="ltr">
                                {lang === "ar" ? "انقر داخل حقل نصي أولاً" : lang === "fr" ? "Cliquez d'abord dans un champ texte" : "Click inside a text field first, then tap a key."}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
